import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { setGlobalDateFormat, type AppDateFormat } from '@/lib/formatters';

export interface OrganizationSettings {
  company_name: string;
  timezone: string;
  date_format: string;
  theme: string;
  compact_mode: boolean;
  data_retention: string;
  email_alerts: boolean;
  in_app_alerts: boolean;
  digest_frequency: string;
  currency: string;
  icp_config: any | null;
}

const DEFAULT_SETTINGS: OrganizationSettings = {
  company_name: '',
  timezone: 'America/New_York',
  date_format: 'MM/DD/YYYY',
  theme: 'system',
  compact_mode: false,
  data_retention: 'forever',
  email_alerts: true,
  in_app_alerts: true,
  digest_frequency: 'daily',
  currency: 'USD',
  icp_config: null,
};

interface OrganizationSettingsContextType {
  settings: OrganizationSettings;
  isLoading: boolean;
  isSaving: boolean;
  saveSettings: (newSettings: OrganizationSettings) => Promise<boolean>;
  updateSetting: <K extends keyof OrganizationSettings>(key: K, value: OrganizationSettings[K]) => void;
}

const OrganizationSettingsContext = createContext<OrganizationSettingsContextType | undefined>(undefined);

export function OrganizationSettingsProvider({ children }: { children: ReactNode }) {
  const { organization } = useAuth();
  const [settings, setSettings] = useState<OrganizationSettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!organization?.id) {
      setIsLoading(false);
      return;
    }

    const load = async () => {
      try {
        const { data, error } = await supabase
          .from('organization_settings')
          .select('*')
          .eq('organization_id', organization.id)
          .maybeSingle();

        if (error) console.error('[Settings] Load error:', error);

        if (data) {
          const loaded: OrganizationSettings = {
            company_name: data.company_name ?? DEFAULT_SETTINGS.company_name,
            timezone: data.timezone ?? DEFAULT_SETTINGS.timezone,
            date_format: data.date_format ?? DEFAULT_SETTINGS.date_format,
            theme: data.theme ?? DEFAULT_SETTINGS.theme,
            compact_mode: data.compact_mode ?? DEFAULT_SETTINGS.compact_mode,
            data_retention: data.data_retention ?? DEFAULT_SETTINGS.data_retention,
            email_alerts: data.email_alerts ?? DEFAULT_SETTINGS.email_alerts,
            in_app_alerts: data.in_app_alerts ?? DEFAULT_SETTINGS.in_app_alerts,
            digest_frequency: data.digest_frequency ?? DEFAULT_SETTINGS.digest_frequency,
            currency: data.currency ?? DEFAULT_SETTINGS.currency,
            icp_config: data.icp_config ?? null,
          };
          setSettings(loaded);
          setGlobalDateFormat(loaded.date_format as AppDateFormat);
          applyTheme(loaded.theme);
          applyCompactMode(loaded.compact_mode);
        } else {
          setSettings(prev => ({ ...prev, company_name: organization.name || '' }));
        }
      } catch (err) {
        console.error('[Settings] Failed to load:', err);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [organization?.id]);

  const saveSettings = useCallback(async (newSettings: OrganizationSettings) => {
    if (!organization?.id) return false;
    setIsSaving(true);

    try {
      const { error } = await supabase
        .from('organization_settings')
        .upsert({ organization_id: organization.id, ...newSettings }, { onConflict: 'organization_id' });

      if (error) {
        console.error('[Settings] Save error:', error);
        return false;
      }

      setSettings(newSettings);
      setGlobalDateFormat(newSettings.date_format as AppDateFormat);
      applyTheme(newSettings.theme);
      applyCompactMode(newSettings.compact_mode);
      return true;
    } catch (err) {
      console.error('[Settings] Save failed:', err);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [organization?.id]);

  const updateSetting = useCallback(<K extends keyof OrganizationSettings>(key: K, value: OrganizationSettings[K]) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    if (key === 'date_format') setGlobalDateFormat(value as AppDateFormat);
    if (key === 'theme') applyTheme(value as string);
    if (key === 'compact_mode') applyCompactMode(value as boolean);
  }, []);

  return (
    <OrganizationSettingsContext.Provider value={{ settings, isLoading, isSaving, saveSettings, updateSetting }}>
      {children}
    </OrganizationSettingsContext.Provider>
  );
}

export function useOrganizationSettings() {
  const context = useContext(OrganizationSettingsContext);
  if (!context) {
    throw new Error('useOrganizationSettings must be used within OrganizationSettingsProvider');
  }
  return context;
}

function applyTheme(theme: string) {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  if (theme === 'light' || theme === 'dark') {
    root.classList.add(theme);
  } else {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    root.classList.add(prefersDark ? 'dark' : 'light');
  }
}

function applyCompactMode(compact: boolean) {
  document.body.classList.toggle('compact-mode', compact);
}
